import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { GlassCard } from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { FileUp, Loader2 } from "lucide-react";

export default function PdfImport() {
  const [files, setFiles] = useState([]);
  const [mode, setMode] = useState("steps");
  const [partNumber, setPartNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) return setErr("Select at least one PDF");
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("mode", mode);
      if (partNumber.trim()) fd.append("part_number", partNumber.trim());
      const { data } = await api.post("/v1/imports/pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
    } catch (ex) {
      setErr(formatApiError(ex?.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="pdf-import-page">
      <header>
        <h1 className="font-display text-3xl font-semibold text-white">PDF Import</h1>
        <p className="text-sm text-[#9AA4B2] mt-1">
          Import one or many PDFs as draft work instructions. Always lands in draft for review.
        </p>
      </header>

      <GlassCard className="p-6">
        <form onSubmit={onSubmit} className="space-y-4">
          <div
            className="rounded-2xl border border-dashed border-white/15 bg-white/[0.03] p-8 text-center cursor-pointer hover:bg-white/[0.05]"
            onClick={() => inputRef.current?.click()}
          >
            <FileUp className="mx-auto text-indigo-300" size={28} />
            <div className="mt-2 text-white text-sm">
              {files.length ? `${files.length} file(s) selected` : "Drop or click to select PDFs"}
            </div>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Mode</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className="mt-1 w-full rounded-full bg-white/[0.04] border border-white/10 px-4 py-2.5 text-white"
            >
              <option value="steps">Merge → one WI with structured steps</option>
              <option value="attach">Merge → one WI, attachments only</option>
              <option value="bulk">Bulk → one draft WI per PDF</option>
            </select>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[#9AA4B2]">Part number (optional)</label>
            <input
              value={partNumber}
              onChange={(e) => setPartNumber(e.target.value)}
              className="mt-1 w-full rounded-full bg-white/[0.04] border border-white/10 px-4 py-2.5 text-white"
              placeholder="ABC-123"
            />
          </div>

          {err && <div className="text-sm text-rose-300">{err}</div>}

          <Button type="submit" disabled={busy} className="btn-primary-glow rounded-full w-full">
            {busy ? (
              <>
                <Loader2 className="animate-spin mr-2" size={16} /> Importing…
              </>
            ) : (
              "Import PDFs"
            )}
          </Button>
        </form>
      </GlassCard>

      {result && (
        <GlassCard className="p-6 space-y-3" data-testid="pdf-import-result">
          <div className="text-emerald-300 text-sm">Import {result.status}</div>
          <ul className="space-y-2">
            {(result.sops_created || []).map((s) => (
              <li key={s.sop_id}>
                <button
                  className="text-cyan-300 hover:underline text-sm"
                  onClick={() => navigate(`/sops/${s.sop_id}/edit`)}
                >
                  {s.title}
                </button>
              </li>
            ))}
          </ul>
          {(result.warnings || []).length > 0 && (
            <div className="text-xs text-amber-300">{result.warnings.join(" · ")}</div>
          )}
        </GlassCard>
      )}
    </div>
  );
}
